const emptyParagraph = () => [{ type: 'p', children: [{ text: '' }] }];

const clone = (value) => JSON.parse(JSON.stringify(value));

const normalizeAlignment = (value) => {
  const alignment = value?.trim().toLowerCase();
  return ['left', 'center', 'right', 'justify'].includes(alignment)
    ? alignment
    : undefined;
};

const getClassStyle = (element, property, classStyles) => {
  for (const className of Array.from(element.classList || [])) {
    const value = classStyles.get(className)?.[property];
    if (value) return value;
  }
  return undefined;
};

const getStyle = (element, property, classStyles) =>
  element.style[property] || getClassStyle(element, property, classStyles);

const getAlignment = (cell, classStyles) => {
  const alignedElement = cell.querySelector('[style*="text-align"], [align]');
  return normalizeAlignment(
    getStyle(cell, 'textAlign', classStyles) ||
      cell.getAttribute('align') ||
      (alignedElement && getStyle(alignedElement, 'textAlign', classStyles)) ||
      alignedElement?.getAttribute('align'),
  );
};

const getInlineFormats = (element, classStyles) => {
  const tag = element.tagName;
  const weight = getStyle(element, 'fontWeight', classStyles)?.toLowerCase();
  return {
    strong:
      tag === 'B' ||
      tag === 'STRONG' ||
      weight === 'bold' ||
      Number.parseInt(weight, 10) >= 600,
    em:
      tag === 'I' ||
      tag === 'EM' ||
      getStyle(element, 'fontStyle', classStyles)?.toLowerCase() === 'italic',
  };
};

const wrapInlineFormats = (children, formats) => {
  if (!children.length) return children;

  let wrapped = children;
  if (formats.em) wrapped = [{ type: 'em', children: wrapped }];
  if (formats.strong) wrapped = [{ type: 'strong', children: wrapped }];
  return wrapped;
};

const parseInlineNodes = (parent, classStyles) =>
  Array.from(parent.childNodes).flatMap((node) => {
    if (node.nodeType === 3) {
      return node.nodeValue ? [{ text: node.nodeValue }] : [];
    }
    if (node.nodeType !== 1) return [];
    if (node.tagName === 'BR') return [{ text: '\n' }];

    const children = parseInlineNodes(node, classStyles);
    return wrapInlineFormats(children, getInlineFormats(node, classStyles));
  });

const compactInlineNodes = (nodes) => {
  const compacted = [];
  nodes.forEach((node) => {
    const previous = compacted[compacted.length - 1];
    if (node.text !== undefined && previous?.text !== undefined) {
      previous.text += node.text;
    } else {
      compacted.push(node);
    }
  });
  return compacted.length ? compacted : [{ text: '' }];
};

const parseCellValue = (cell, classStyles) => {
  const alignment = getAlignment(cell, classStyles);
  const cellFormats = getInlineFormats(cell, classStyles);
  const directBlocks = Array.from(cell.children).filter((element) =>
    ['P', 'DIV'].includes(element.tagName),
  );
  const blocks = directBlocks.length ? directBlocks : [cell];

  return blocks.map((block) => ({
    type: 'p',
    ...(normalizeAlignment(getStyle(block, 'textAlign', classStyles)) ||
    alignment
      ? {
          textAlign:
            normalizeAlignment(getStyle(block, 'textAlign', classStyles)) ||
            alignment,
        }
      : {}),
    children: compactInlineNodes(
      wrapInlineFormats(
        parseInlineNodes(block, classStyles),
        blocks.length === 1 && block === cell
          ? cellFormats
          : {
              strong:
                cellFormats.strong ||
                getInlineFormats(block, classStyles).strong,
              em: cellFormats.em || getInlineFormats(block, classStyles).em,
            },
      ),
    ),
  }));
};

const collectClassStyles = (document) => {
  const classStyles = new Map();
  const collectRule = (selectorText, style) => {
    const classNames = selectorText?.match(/\.([a-zA-Z0-9_-]+)/g) || [];
    classNames.forEach((classSelector) => {
      const className = classSelector.slice(1);
      classStyles.set(className, {
        ...(classStyles.get(className) || {}),
        fontWeight: style.fontWeight,
        fontStyle: style.fontStyle,
        textAlign: style.textAlign,
      });
    });
  };
  const styleSheets = [
    ...Array.from(document.styleSheets || []),
    ...Array.from(document.querySelectorAll('style'))
      .map((element) => element.sheet)
      .filter(Boolean),
  ];
  Array.from(new Set(styleSheets)).forEach((styleSheet) => {
    let rules = [];
    try {
      rules = Array.from(styleSheet.cssRules || []);
    } catch {
      return;
    }
    rules.forEach((rule) => collectRule(rule.selectorText, rule.style));
  });

  Array.from(document.querySelectorAll('style')).forEach((element) => {
    const simpleRulePattern = /([^{}]+)\{([^{}]*)\}/g;
    for (const match of element.textContent.matchAll(simpleRulePattern)) {
      const probe = document.createElement('span');
      probe.style.cssText = match[2];
      collectRule(match[1], probe.style);
    }
  });
  return classStyles;
};

const inferSource = (html = '') => {
  const normalized = html.toLowerCase();
  if (normalized.includes('google-sheets-html-origin')) return 'google-sheets';
  if (
    normalized.includes('mso-') ||
    normalized.includes('urn:schemas-microsoft-com:office')
  ) {
    return 'excel';
  }
  if (
    normalized.includes('libreoffice') ||
    normalized.includes('openoffice') ||
    normalized.includes('generator="libreoffice')
  ) {
    return 'libreoffice-calc';
  }
  return 'unknown';
};

const parseSpan = (cell, attribute, warnings) => {
  const rawValue = cell.getAttribute(attribute);
  if (!rawValue) return 1;
  const value = Number.parseInt(rawValue, 10);
  if (!Number.isInteger(value) || value < 1) {
    warnings.push(`Valor inválido de ${attribute}: ${rawValue}`);
    return 1;
  }
  return value;
};

const hasOccupiedCells = (row, start, length) => {
  for (let offset = 0; offset < length; offset += 1) {
    if (row[start + offset]) return true;
  }
  return false;
};

const parseHtmlGrid = (html) => {
  const document = new DOMParser().parseFromString(html, 'text/html');
  const table = document.querySelector('table');
  if (!table) return null;

  const warnings = [];
  const classStyles = collectClassStyles(document);
  const grid = [];
  const rows = Array.from(table.querySelectorAll('tr')).filter(
    (row) => row.closest('table') === table,
  );

  rows.forEach((row, rowIndex) => {
    grid[rowIndex] ||= [];
    let columnIndex = 0;
    const cells = Array.from(row.children).filter((cell) =>
      ['TD', 'TH'].includes(cell.tagName),
    );

    cells.forEach((cell) => {
      const rowSpan = parseSpan(cell, 'rowspan', warnings);
      const columnSpan = parseSpan(cell, 'colspan', warnings);
      while (hasOccupiedCells(grid[rowIndex], columnIndex, columnSpan)) {
        columnIndex += 1;
      }
      const value = parseCellValue(cell, classStyles);

      for (let rowOffset = 0; rowOffset < rowSpan; rowOffset += 1) {
        const targetRow = rowIndex + rowOffset;
        grid[targetRow] ||= [];
        for (
          let columnOffset = 0;
          columnOffset < columnSpan;
          columnOffset += 1
        ) {
          grid[targetRow][columnIndex + columnOffset] =
            rowOffset === 0 && columnOffset === 0
              ? clone(value)
              : emptyParagraph();
        }
      }
      columnIndex += columnSpan;
    });
  });

  const columns = Math.max(0, ...grid.map((row) => row.length));
  grid.forEach((row) => {
    if (row.length !== columns || row.some((cell) => !cell)) {
      warnings.push('A grade irregular foi preenchida com células vazias.');
    }
    for (let column = 0; column < columns; column += 1) {
      row[column] ||= emptyParagraph();
    }
  });

  return { grid, warnings, source: inferSource(html), format: 'html' };
};

const parseTsvGrid = (text) => {
  if (!text?.includes('\t')) return null;
  const lines = text.replace(/\r\n?/g, '\n').split('\n');
  if (lines[lines.length - 1] === '') lines.pop();
  const columns = Math.max(...lines.map((line) => line.split('\t').length));
  const grid = lines.map((line) => {
    const cells = line
      .split('\t')
      .map((value) => [{ type: 'p', children: [{ text: value }] }]);
    while (cells.length < columns) cells.push(emptyParagraph());
    return cells;
  });
  return {
    grid,
    warnings: [
      'A colagem usou texto tabulado; formatações não estão disponíveis.',
    ],
    source: 'unknown',
    format: 'tsv',
  };
};

export const parseSpreadsheetClipboard = ({ html = '', text = '' }) => {
  const htmlResult = html ? parseHtmlGrid(html) : null;
  const result = htmlResult || parseTsvGrid(text);
  if (!result?.grid.length || !result.grid[0]?.length) {
    return { handled: false };
  }
  return { handled: true, ...result };
};

export const readRichClipboardPayload = async (payload, clipboardApi) => {
  if (!clipboardApi?.read) {
    return {
      ...payload,
      clipboardRead: { attempted: false, status: 'unavailable' },
    };
  }

  try {
    const items = await clipboardApi.read();
    const formats = { ...(payload.formats || {}) };
    for (const item of items) {
      for (const type of item.types.filter((itemType) =>
        itemType.startsWith('text/'),
      )) {
        formats[type] = await (await item.getType(type)).text();
      }
    }
    return {
      ...payload,
      types: Object.keys(formats),
      html: formats['text/html'] || payload.html || '',
      text: formats['text/plain'] || payload.text || '',
      formats,
      clipboardRead: { attempted: true, status: 'success' },
    };
  } catch (error) {
    return {
      ...payload,
      clipboardRead: {
        attempted: true,
        status: 'error',
        message: error?.message,
      },
    };
  }
};

const createCell = (value, type, getId) => ({
  key: getId(),
  type,
  value: clone(value),
});

export const mergePastedGrid = (table, grid, startRow, startColumn, getId) => {
  const rows = clone(table?.rows || []);
  const currentColumns = Math.max(
    0,
    ...rows.map((row) => row.cells?.length || 0),
  );
  const pastedColumns = Math.max(0, ...grid.map((row) => row.length));
  const finalColumns = Math.max(currentColumns, startColumn + pastedColumns);
  const finalRows = Math.max(rows.length, startRow + grid.length);

  while (rows.length < finalRows) {
    const rowIndex = rows.length;
    rows.push({
      key: getId(),
      cells: Array.from({ length: finalColumns }, () =>
        createCell(emptyParagraph(), rowIndex === 0 ? 'header' : 'data', getId),
      ),
    });
  }

  rows.forEach((row, rowIndex) => {
    row.cells ||= [];
    while (row.cells.length < finalColumns) {
      row.cells.push(
        createCell(emptyParagraph(), rowIndex === 0 ? 'header' : 'data', getId),
      );
    }
  });

  grid.forEach((pastedRow, rowOffset) => {
    pastedRow.forEach((value, columnOffset) => {
      rows[startRow + rowOffset].cells[startColumn + columnOffset].value =
        clone(value);
    });
  });

  return { ...table, rows };
};

export const createTablePasteReport = ({
  payload,
  result,
  target,
  finalTable,
}) => ({
  source: result.source,
  format: result.format,
  clipboardTypes: payload.types,
  clipboardRead: payload.clipboardRead,
  input: {
    html: payload.html,
    text: payload.text,
    formats: payload.formats,
  },
  parsedGrid: result.grid,
  warnings: result.warnings,
  target,
  finalTable,
});

export const logTablePasteReport = (report) => {
  // eslint-disable-next-line no-console
  console.groupCollapsed('[govbr] Colagem de tabela');
  // eslint-disable-next-line no-console
  console.log(report);
  // eslint-disable-next-line no-console
  console.groupEnd();
};
